import { action, makeAutoObservable } from 'mobx';
import EventEmitter from 'events';

import { VisibilityStore } from 'src/modules/users/stores/VisibilityStore';
import ToastifyService from 'src/services/ToastifyService';

import type {
  IUserBlockingReason,
  IUserProfile,
  IUserProfileEditFrontend,
  IUserProfileServiceProvider,
} from '../interfaces';

export type TUserTypes = 'customer' | 'contractor' | 'employee' | 'partner';

export class UserWidgetStore {
  private profileService?: IUserProfileServiceProvider;

  readonly blockingModal = new VisibilityStore();
  readonly unblockingModal = new VisibilityStore();
  readonly revocationModal = new VisibilityStore();

  eventEmitter: EventEmitter | undefined;

  userType: TUserTypes | undefined;

  isLoading = false;
  isEditLoading = true;
  blockProfileInProgress = false;
  unblockProfileInProgress = false;
  revocationPersonalDataInProgress = false;
  profile?: IUserProfile;
  formData?: IUserProfileEditFrontend;
  isEditOpen = false;

  userId: string | undefined;

  hasError = false;

  constructor() {
    makeAutoObservable(this, undefined, { autoBind: true });
  }

  init({
    userId,
    profileService,
    userType,
    eventEmitter,
  }: {
    userId: string;
    profileService: IUserProfileServiceProvider;
    userType: TUserTypes;
    eventEmitter: EventEmitter;
  }) {
    this.profileService = profileService;
    this.userId = userId;
    this.fetchProfile();
    this.userType = userType;
    this.eventEmitter = eventEmitter;

    this.eventEmitter.on('profile/updated', this.fetchProfile);

    return this;
  }

  unload() {
    this.profile = undefined;
    this.formData = undefined;

    this.profileService = undefined;

    this.eventEmitter?.off('profile/updated', this.fetchProfile);
    this.eventEmitter = undefined;
  }

  fetchProfile() {
    this.isLoading = true;
    this.hasError = false;

    this.profileService!.getById({ id: this.userId! })
      .then(
        action((profile) => {
          this.profile = profile;
        }),
      )
      .catch(
        action(() => {
          this.hasError = true;
        }),
      )
      .finally(
        action(() => {
          this.isLoading = false;
        }),
      );
  }

  showEditForm() {
    if (!this.profile?.id) return;

    this.isEditOpen = true;
    this.isEditLoading = true;

    this.profileService!.retrieveEditUser({ id: this.profile.id })
      .then(
        action((formData) => {
          this.formData = formData;
        }),
      )
      .finally(
        action(() => {
          this.isEditLoading = false;
        }),
      );
  }

  onSubmitEditForm(formData: IUserProfileEditFrontend) {
    this.profileService!.update(formData)
      .then(
        action((profile) => {
          this.isEditOpen = false;
          this.profile = profile;

          ToastifyService.toast(`Информация по клиенту ${profile.fullName} успешно обновлена`, { type: 'success' });
        }),
      )
      .catch(() => ToastifyService.toast('Возникла техническая ошибка, попробуйте позже', { type: 'error' }));
  }

  closeEditForm() {
    this.isEditOpen = false;
  }

  searchBlockingReasons = (searchString: string) => this.profileService!.searchBlockingReasons({ searchString });

  // async searchTenants(searchString: string) {
  //   const { data } = await this.profileService!.searchTenants({ searchString });

  //   return data || [];
  // }

  onSubmitBlock(data: { reason: IUserBlockingReason | null }) {
    if (!data.reason || !this.profile) return;

    const { value: blockingReasonSlug } = data.reason;
    this.blockProfileInProgress = true;

    return this.profileService!.block({
      id: this.profile.id!,
      blockingReasonSlug,
    })
      .then(
        action((profile) => {
          this.blockingModal.hide();
          this.profile = profile;

          ToastifyService.toast(`Клиент ${profile.fullName} успешно заблокирован`, { type: 'success' });
        }),
      )
      .catch(() => ToastifyService.toast('Возникла техническая ошибка, попробуйте позже', { type: 'error' }))
      .finally(() => (this.blockProfileInProgress = false));
  }

  onSubmitUnblock() {
    if (!this.profile?.id) return;

    this.unblockProfileInProgress = true;
    this.profileService!.unblock({ id: this.profile.id })
      .then(
        action((data) => {
          this.unblockingModal.hide();
          this.profile = data;

          ToastifyService.toast(`Клиент ${this.profile?.fullName} успешно разблокирован`, { type: 'success' });
        }),
      )
      .catch(() => ToastifyService.toast('Возникла техническая ошибка, попробуйте позже', { type: 'error' }))
      .finally(() => (this.unblockProfileInProgress = false));
  }

  onUnblockUserClick() {
    this.unblockingModal.show();
  }

  onRevocationPersonalDataUserClick() {
    this.revocationModal.show();
  }

  onRevocationPersonalDataClick() {
    if (!this.profile?.id) return;

    this.revocationPersonalDataInProgress = true;

    this.profileService
      ?.revocationPersonalData({ id: this.profile.id })
      .then((data) => {
        this.profile = data;
        this.revocationModal.hide();
        ToastifyService.toast(`Персональные данные ${this.profile?.fullName} успешно отозваны`, { type: 'success' });
      })
      .catch(() => ToastifyService.toast('Возникла техническая ошибка, попробуйте позже', { type: 'error' }))
      .finally(() => (this.revocationPersonalDataInProgress = false));
  }
}
